/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

'use client';

import { useCallback, useRef } from 'react';

import { useNotificationProvider } from './notification-provider.component';

type NotifyFn = ReturnType<typeof useNotificationProvider>['notify'];
type NotifyFnParams = Parameters<NotifyFn>;

/**
 * A simple wrapper hook that will automatically dismiss the created toast
 * notification after the configured amount of time.
 *
 * @returns notify callback that will create the notification
 *
 * @example
 *   const notifyErrorWithTimeout = useNotificationWithTimeout({
 *     timeout: 3000,
 *     notifyArgs: [
 *       'Oops! Please try again later',
 *       NotificationType.Error,
 *       true,
 *       NotificationScope.Page,
 *     ],
 *   });
 */
export function useNotificationWithTimeout({
  timeout = 3000,
  notifyArgs,
}: {
  timeout?: number;
  notifyArgs: NotifyFnParams;
}): () => void {
  const { notify, remove } = useNotificationProvider();

  const notificationRef = useRef<ReturnType<NotifyFn> | null>(null);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  return useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }

    if (notificationRef.current != null) {
      remove(notificationRef.current);
      notificationRef.current = null;
    }

    notificationRef.current = notify(...notifyArgs);

    timeoutIdRef.current = setTimeout(() => {
      if (notificationRef.current != null) {
        remove(notificationRef.current);
        notificationRef.current = null;
      }
    }, timeout);
  }, [notifyArgs, notify, remove, timeout]);
}
